import { BrowserSupport } from '@tableau/browser-support';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';
import { useLocation } from 'react-router-dom';
import _ from 'underscore';

import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { usePrevious } from '~src/bootstrap/hooks';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { FilterPanelSection } from '~src/spacesPlatform/components/filterPanel/FilterPanelSection';
import { clearAllButton, filterPanelContainer } from '~src/spacesPlatform/components/filterPanel/FilterPanelStyles';
import { SearchField } from '~src/spacesPlatform/components/filterPanel/SearchField';
import { ContentFilterKeyName, ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { DatabaseWithCRASSContentType, DatasourceContentType, TableWithCRASSContentType, ViewContentType, WorkbookContentType } from '~src/spacesPlatform/models/ContentTypes';
import { OrderKeys } from '~src/spacesPlatform/models/OrderKeys';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { FilterCategory, IFilterDefinition } from '~src/spacesPlatform/services/FilterPanelService';
import { isSomeKindaSearchSpace } from '~src/spacesPlatform/state/SpaceInfo';

export interface IUrlParams {
  search?: string;
  order?: string;
}

export interface IFilterControl {
  definition: IFilterDefinition<any>;
  value: any;
}

export interface FilterPanelProps {
  /** for FilterPanelResizer -- the events/state belong to Explorer but need to render it here for proper scrollable height */
  readonly children?: React.ReactNode;
  readonly contentType: string;
  readonly filterDefinitions?: IFilterDefinition<any>[];
  readonly hideSearch?: boolean;
  readonly getFieldValueQuery: (filterField: string) => FieldValuesQuery;
}

export function FilterPanel(props: FilterPanelProps) {
  const location = useLocation();
  const previousLocation = usePrevious(location);

  const [anyFiltersActive, setAnyFiltersActive] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState<string>('');
  const [shouldClear, setShouldClear] = React.useState<boolean>(false);
  const [filters, setFilters] = React.useState<IFilterControl[]>([]);

  const updateModelFromURL = React.useCallback(async () => {
    const urlParams: IUrlParams = WindowLocationService.getHashQueryObject();
    const newFilters: IFilterControl[] = makeFilters(props.filterDefinitions);
    for (let filter of newFilters) {
      filter.value = await filter.definition.valueFromParams(urlParams);
    }

    if (!_.isEqual(newFilters, filters)) {
      setFilters(newFilters);
    }

    if (!_.isEqual(location, previousLocation)) {
      const urlParamsKeys = _.keys(urlParams);
      setAnyFiltersActive(findAnyFiltersActive(urlParamsKeys));
      setSearchText(urlParams.search || '');
    }
  }, [location, previousLocation, props.filterDefinitions, filters]);

  React.useEffect(
    () => {
      updateModelFromURL();
    },
    [updateModelFromURL]
  );

  // After component updates and shouldClear is true, set shouldClear to false
  // because clear is treated as one-time event. Setting the value back to false
  // allows the user to clear again at a later point.
  React.useEffect(
    () => {
      shouldClear && setShouldClear(false);
    },
    [shouldClear]
  );

  const clearAllFilters = React.useCallback(() => {
    const urlParams = WindowLocationService.getHashQueryObject();
    const inSearchSpace = isSomeKindaSearchSpace(selectSpaceInfo(getAppStore().getState()));
    _.each(_.keys(urlParams), (key: string) => {
      if (_.has(ContentFiltersKeys, key)) {
        if (inSearchSpace && key === ContentFiltersKeys.search) {
          return;
        }
        updateUrlParam(key);
      }
    });

    // clear search filter
    setSearchText('');
    // Other filters
    setShouldClear(true);

    // A text search filter automatically orders the results by relevance;
    // clear that extra sort order when clearing the filters
    if (shouldBeSortedByRelevanceOnSearch(props.contentType)) {
      removeRelevanceFromOrder();
    }
  }, [props.contentType]);

  const submitSearch = React.useCallback((searchText: string, event?: React.KeyboardEvent<HTMLInputElement>) => {
    updateUrlParam(ContentFiltersKeys.search, searchText);

    // A text search filter automatically orders the results by relevance
    if (shouldBeSortedByRelevanceOnSearch(props.contentType)) {
      if (searchText) {
        updateUrlParam(UrlParamKeys.Order, Order.serialize([Order.desc(OrderKeys.relevance)]));
      } else {
        removeRelevanceFromOrder();
      }
    }

    if (BrowserSupport.IsTouch() && event) {
      $(event.target).trigger('blur');
    }
  }, [props.contentType]);

  const filterPanelContent = React.useMemo(
    () => {
      const detailSectionFields = getFiltersForSection(filters, 'detail');
      const dateSectionFields = getFiltersForSection(filters, 'date');
      const dataSectionFields = getFiltersForSection(filters, 'data');
      const dataSourceSectionFields = getFiltersForSection(filters, 'datasource');
      const moreSectionFields = getFiltersForSection(filters, 'more');

      return <div id={'filter-panel-container'} className={filterPanelContainer}>
        <ButtonWidget
          buttonType={ButtonType.LowEmphasis}
          children={Messages.clearAllFilters()}
          className={clearAllButton}
          disabled={!anyFiltersActive}
          onClick={clearAllFilters}
          testId={'clear-all-filters'}
        />
        {!props.hideSearch &&
          <SearchField
            searchText={searchText}
            setSearchText={setSearchText}
            submitSearch={submitSearch}
          />
        }
        {!_.isEmpty(detailSectionFields) &&
          <FilterPanelSection
            sectionHeaderText={Messages.details()}
            testId="Details"
            filters={detailSectionFields}
            shouldClear={shouldClear}
            getFieldValueQuery={props.getFieldValueQuery}
          />
        }
        {!_.isEmpty(dateSectionFields) &&
          <FilterPanelSection
            sectionHeaderText={Messages.date()}
            testId="Date"
            filters={dateSectionFields}
            shouldClear={shouldClear}
          />
        }
        {!_.isEmpty(dataSectionFields) &&
          <FilterPanelSection
            sectionHeaderText={Messages.data()}
            testId="Data"
            filters={dataSectionFields}
            shouldClear={shouldClear}
          />
        }
        {!_.isEmpty(dataSourceSectionFields) &&
          <FilterPanelSection
            sectionHeaderText={Messages.datasource()}
            testId="DataSource"
            filters={dataSourceSectionFields}
            shouldClear={shouldClear}
            getFieldValueQuery={props.getFieldValueQuery}
          />
        }
        {!_.isEmpty(moreSectionFields) &&
          <FilterPanelSection
            sectionHeaderText={Messages.moreFilters()}
            testId="More"
            filters={moreSectionFields}
            shouldClear={shouldClear}
          />
        }
        {props.children}
      </div>;
    },
    [anyFiltersActive, props.children, clearAllFilters, filters, props.getFieldValueQuery, props.hideSearch, searchText, shouldClear, submitSearch]
  );

  return filterPanelContent;
}

function makeFilters(filterDefinitions: IFilterDefinition<any>[]): IFilterControl[] {
  return _.map(filterDefinitions, (defn: IFilterDefinition<any>): IFilterControl => {
    return { definition: defn, value: undefined } as IFilterControl;
  });
}

// exported for easier testing
export function removeRelevanceFromOrder() {
  const orderValue = (WindowLocationService.getHashQueryObject() as IUrlParams)?.order;
  if (orderValue) {
    // The user may have added other order params, so we only remove relevance.
    let order: ReadonlyArray<IOrderClause> = Order.fromInput(orderValue);
    order = Order.withoutField(order, OrderKeys.relevance);

    const serializedOrder = Order.serialize(order);

    updateUrlParam(UrlParamKeys.Order, serializedOrder === ''? null : serializedOrder);
  }
}

/**
 * Returns true if any filters are active from the URL.
 * @param urlParamsKeys - A list of current param keys in url
 * @returns {boolean}
 *
 * exported for easier testing
 */
export function findAnyFiltersActive(urlParamsKeys: string[]): boolean {
  if(_.isEmpty(urlParamsKeys)) {
    return false;
  }

  const filtersToRemove: ContentFilterKeyName[] = [];

  // Account for the search space scenario in which the search space is the only filter set. In this case we don't count it as an active filter
  if (isSomeKindaSearchSpace(selectSpaceInfo(getAppStore().getState()))) {
    filtersToRemove.push(ContentFiltersKeys.search);
  }

  if (getSessionManagementServiceInstance().isLimitedUserVisibilityEnforced()) {
    filtersToRemove.push(ContentFiltersKeys.owner);
  }

  const filtersToKeep = _.difference(_.values(ContentFiltersKeys), filtersToRemove);

  return _.some(urlParamsKeys, (key: string) => {
    return _.contains(filtersToKeep, key);
  });
}

function getFiltersForSection(filters: IFilterControl[], category: FilterCategory) {
  return filters.filter(
    (control: IFilterControl) => control.definition.category === category);
}

export function submitUrlQuery(params: any) {
  _.each(params, (value: any, key: string) => {
    updateUrlParam(key, value);
  });
}

export function updateUrlParam(key: string, value?: string | null) {
  // TODO Should we do any processing of 'value' before sticking it into the url?
  if (value) {
    WindowLocationService.setHashQueryParameter(key, value);
  } else {
    WindowLocationService.deleteQueryParameter(key);
  }
}

function shouldBeSortedByRelevanceOnSearch(type: string) {
  return type === WorkbookContentType.name || type === ViewContentType.name ||
         type === DatasourceContentType.name || type === DatabaseWithCRASSContentType.name ||
         type === TableWithCRASSContentType.name;
}
